<template>
  <span
    v-if="normalizedCount > 0"
    class="notification-badge"
    :title="accessibleText"
  >
    <span aria-hidden="true">{{ displayCount }}</span>
    <span class="notification-badge__sr-only">{{ accessibleText }}</span>
  </span>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  count: {
    type: Number,
    default: 0,
  },
  singularLabel: {
    type: String,
    default: 'item requires attention',
  },
  pluralLabel: {
    type: String,
    default: 'items require attention',
  },
});

const normalizedCount = computed(() => {
  const count = Number(props.count);
  return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
});

const displayCount = computed(() => (
  normalizedCount.value > 99 ? '99+' : String(normalizedCount.value)
));

const accessibleText = computed(() => (
  `${normalizedCount.value} ${normalizedCount.value === 1 ? props.singularLabel : props.pluralLabel}`
));
</script>

<style scoped>
.notification-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 20px;
  height: 16px;
  padding: 0 6px;
  box-sizing: border-box;
  border-radius: 999px;
  background: var(--noc-red, #c45151);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  /* The count usually arrives after the menu, so it settles in rather than popping up. */
  animation: notification-badge-enter 360ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

@keyframes notification-badge-enter {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
}

@media (prefers-reduced-motion: reduce) {
  .notification-badge {
    animation: none;
  }
}

.notification-badge__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
